import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/axiosConfig';

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { logout } = useAuth();

  const apiCall = useCallback(async (method, endpoint, data = null, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const config = {
        method,
        url: endpoint,
        ...options
      };

      if (data && ['post', 'put', 'patch'].includes(method.toLowerCase())) {
        config.data = data;
      }

      const response = await api(config);
      return response.data;
    } catch (err) {
      console.error('API Error:', err);
      const endpointPath = endpoint || '';
      const isLoginEndpoint = endpointPath.includes('/login');
      
      if (err.response?.status === 401 && !isLoginEndpoint) {
        // Token expired or invalid
        logout();
        throw new Error('Session expired. Please login again.');
      }
      
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'An error occurred';
      setError(errorMessage);
      const wrappedError = new Error(errorMessage);
      wrappedError.response = err.response;
      wrappedError.code = err.code;
      wrappedError.isCorsError = err.isCorsError;
      throw wrappedError;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const get = useCallback((endpoint, options = {}) => 
    apiCall('get', endpoint, null, options), [apiCall]);
  
  const post = useCallback((endpoint, data, options = {}) => 
    apiCall('post', endpoint, data, options), [apiCall]);
  
  const put = useCallback((endpoint, data, options = {}) => 
    apiCall('put', endpoint, data, options), [apiCall]);
  
  const patch = useCallback((endpoint, data, options = {}) => 
    apiCall('patch', endpoint, data, options), [apiCall]);
  
  const del = useCallback((endpoint, options = {}) => 
    apiCall('delete', endpoint, null, options), [apiCall]);

  return {
    loading,
    error,
    get,
    post,
    put,
    patch,
    delete: del,
    clearError: () => setError(null)
  };
};
